import React from 'react';
import UniversalLineWithComponents from '../../components/components/universal_line_with_components/universalLineWithComponents.jsx';
import customText from '../../components/components/custom_text/customText.jsx';
import { ReactComponent as People } from '../../components/icons/people.svg';
import { ReactComponent as Chart } from '../../components/icons/chart.svg';
import { ReactComponent as DolarSquare } from '../../components/icons/dolar-square.svg';
import { ReactComponent as Play } from '../../components/icons/play.svg';
import { ReactComponent as TasskSquare } from '../../components/icons/task-square.svg';
import { ReactComponent as DingDong } from '../../components/icons/ding-dong.svg';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const LINE_STYLE = 'line-with-components-without-borders';
const VALUE_CLASS = 'custom-text-negative';
const BORDERS = 'LRBT';

function formatNum(n) {
  if (n == null || n === undefined) return '—';
  return Number(n).toLocaleString('pl-PL', { maximumFractionDigits: 2 });
}

function formatPct(n) {
  if (n == null || n === undefined) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function StatLine({ label, value }) {
  return (
    <UniversalLineWithComponents
      basicStyle={LINE_STYLE}
      leftSide={[{ component: customText, props: { text: label, baseClass: 'custom-text' } }]}
      rightSide={[{ component: customText, props: { text: String(value), baseClass: VALUE_CLASS, borders: BORDERS } }]}
    />
  );
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <UniversalLineWithComponents
      basicStyle={LINE_STYLE}
      leftSide={[{ component: customText, props: { icon: Icon, text: title, baseClass: 'custom-text-header' } }]}
      rightSide={[]}
    />
  );
}

const CHART_ACCENT_BLUE = '#1890ff';
const PIE_COLORS = [CHART_ACCENT_BLUE, '#40a9ff', '#69c0ff', '#91d5ff', '#bae7ff', '#e6f7ff'];

export default function StatisticsDashboard({ data }) {
  if (!data) return null;

  const {
    userActivity = {},
    userCounts = {},
    premium = {},
    revenue = {},
    vehicleCheck = {},
    notesAndDocuments = {},
    timesheets = {},
    engagement = {},
  } = data;

  const revenueByCurrency = revenue.revenueByCurrency || {};
  const revenueEntries = Object.entries(revenueByCurrency);
  const summariesByCategory = notesAndDocuments.summariesByCategory || [];
  const summariesByMonth = notesAndDocuments.summariesByMonth || [];
  const avgDocsPerType = notesAndDocuments.averageDocumentsPerTypePerMonth || {};

  const categoryChartData = summariesByCategory.map((item, i) => ({
    name: item.category || `Kategoria ${i + 1}`,
    value: item.count ?? 0,
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  const monthChartData = summariesByMonth.map((item) => ({
    month: item.yearMonth || '',
    count: item.count ?? 0,
  }));

  return (
    <div className="statistics-dashboard">
      {/* 1. Statystyki użytkowników (DAU / WAU / MAU) */}
      <div className="statistics-section-title">
        <SectionHeader icon={Chart} title="Statystyki użytkowników" />
      </div>
      <div className="stats-row-container">
        <div className="stats-column">
          <StatLine label="Aktywni dziennie (DAU)" value={userActivity.dailyActiveUsers} />
          <StatLine label="Aktywni w tygodniu (WAU)" value={userActivity.weeklyActiveUsers} />
          <StatLine label="Aktywni w miesiącu (MAU)" value={userActivity.monthlyActiveUsers} />
        </div>
      </div>

      {/* 2. Użytkownicy */}
      <div className="statistics-section-title">
        <SectionHeader icon={People} title="Użytkownicy" />
      </div>
      <div className="stats-row-container">
        <div className="stats-column">
          <StatLine label="Całkowita liczba użytkowników" value={userCounts.totalUsers} />
          <StatLine label="Nowi użytkownicy (ostatni miesiąc)" value={userCounts.newUsersLastMonth} />
          <StatLine label="Nowi użytkownicy (ostatni tydzień)" value={userCounts.newUsersLastWeek} />
        </div>
      </div>

      {/* 3. Premium i Not Premium */}
      <div className="statistics-section-title">
        <SectionHeader icon={DolarSquare} title="Premium i Not Premium" />
      </div>
      <div className="stats-row-container">
        <div className="stats-column">
          <StatLine label="Użytkownicy premium" value={premium.premiumUsers} />
          <StatLine label="Użytkownicy niepremium" value={premium.nonPremiumUsers} />
          <StatLine label="Kohorta konwersji (30 dni)" value={premium.conversionCohort30Days} />
          <StatLine label="Konwersja na premium (30 dni)" value={premium.convertedToPremium30Days} />
          <StatLine label="Procent konwersji (30 dni)" value={formatPct(premium.conversionRatePercent30Days)} />
        </div>
      </div>

      {/* 4. Finanse */}
      <div className="statistics-section-title">
        <SectionHeader icon={DolarSquare} title="Finanse" />
      </div>
      <div className="stats-row-container">
        <div className="stats-column">
          {revenueEntries.length > 0
            ? revenueEntries.map(([currency, amount]) => (
                <StatLine key={currency} label={`Przychody (${currency})`} value={formatNum(amount)} />
              ))
            : (
              <>
                <StatLine label="Przychody (EUR)" value={formatNum(revenueByCurrency.EUR)} />
                <StatLine label="Przychody (PLN)" value={formatNum(revenueByCurrency.PLN)} />
                <StatLine label="Przychody (GBP)" value={formatNum(revenueByCurrency.GBP)} />
              </>
            )}
          <StatLine label="Mid LTV (średnia wartość użytkownika)" value={formatNum(revenue.midLtv)} />
          <StatLine label="Szacowane przychody (estimated client earnings)" value={formatNum(revenue.estimatedClientEarnings)} />
          <StatLine label="Szacowany koszt (estimated cost)" value={formatNum(revenue.estimatedCost)} />
        </div>
      </div>

      {/* 5. Sprawdzanie pojazdu */}
      <div className="statistics-section-title">
        <SectionHeader icon={Play} title="Sprawdzanie pojazdu" />
      </div>
      <div className="stats-row-container">
        <div className="stats-column">
          <StatLine label="Sprawdzenia początkowe" value={vehicleCheck.initialChecksCount} />
          <StatLine label="Sprawdzenia finalizujące" value={vehicleCheck.finalizingChecksCount} />
          <StatLine label="Procent ukończonych sprawdzeń" value={formatPct(vehicleCheck.percentCompleted)} />
          <StatLine
            label="Średnia liczba dni do finalizacji"
            value={vehicleCheck.averageDaysToFinalization != null ? formatNum(vehicleCheck.averageDaysToFinalization) : '—'}
          />
        </div>
      </div>

      {/* 6. Notatki i dokumenty */}
      <div className="statistics-section-title">
        <SectionHeader icon={TasskSquare} title="Notatki i dokumenty" />
      </div>
      <div className="stats-row-container">
        <div className="stats-column">
          <StatLine label="Średnia dzienna liczba notatek" value={formatNum(notesAndDocuments.averageDailyNotes)} />
          <StatLine label="Średnia liczba dokumentów na użytkownika (mies.)" value={formatNum(notesAndDocuments.averageDocumentsPerUserPerMonth)} />
        </div>
      </div>
      {summariesByCategory.length > 0 && (
        <>
          <UniversalLineWithComponents
            basicStyle="line-with-components-without-borders-top-margin"
            leftSide={[{ component: customText, props: { text: 'Wysłane podsumowania – według kategorii', baseClass: 'custom-text' } }]}
            rightSide={[]}
          />
          <div className="statistics-chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {categoryChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
      {summariesByMonth.length > 0 && (
        <>
          <UniversalLineWithComponents
            basicStyle="line-with-components-without-borders-top-margin"
            leftSide={[{ component: customText, props: { text: 'Wysłane podsumowania – według miesięcy', baseClass: 'custom-text' } }]}
            rightSide={[]}
          />
          <div className="statistics-chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthChartData} margin={{ top: 10, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 0" horizontal vertical={false} />
                <XAxis dataKey="month" />
                <YAxis allowDecimals={false} />
                <Tooltip cursor={{ fill: 'rgba(24, 144, 255, 0.12)' }} />
                <Bar dataKey="count" fill={CHART_ACCENT_BLUE} name="Liczba" barSize={24} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
      {Object.keys(avgDocsPerType).length > 0 && (
        <div className="stats-row-container">
          <div className="stats-column">
            {Object.entries(avgDocsPerType).map(([type, avg]) => (
              <StatLine key={type} label={`Śr. ${type} na użytkownika (mies.)`} value={formatNum(avg)} />
            ))}
          </div>
        </div>
      )}

      {/* 7. Wpisy (timesheets) */}
      <div className="statistics-section-title">
        <SectionHeader icon={TasskSquare} title="Wpisy" />
      </div>
      <div className="stats-row-container">
        <div className="stats-column">
          <StatLine label="Łączna liczba wpisów" value={timesheets.totalEntries} />
          <StatLine label="Wpisy oczekujące (SENT)" value={timesheets.pendingEntries} />
          <StatLine label="Wpisy odrzucone" value={timesheets.rejectedEntries} />
          <StatLine label="Procent wpisów korzystnych (pending)" value={formatPct(timesheets.percentFavourable)} />
          <StatLine label="Procent użytkowników dodających wpisy" value={formatPct(timesheets.percentUsersAddingEntries)} />
        </div>
      </div>

      {/* 8. Zaangażowanie */}
      <div className="statistics-section-title">
        <SectionHeader icon={DingDong} title="Zaangażowanie użytkowników" />
      </div>
      <div className="stats-row-container">
        <div className="stats-column">
          <StatLine label="Użytkownicy z włączonymi notyfikacjami" value={engagement.usersWithNotificationsEnabled} />
          <StatLine label="Procent użytkowników reagujących na notyfikacje" value={formatPct(engagement.percentUsersReactingToNotifications)} />
        </div>
      </div>
    </div>
  );
}
